import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapPin, Loader2, ZoomIn, ZoomOut, Maximize2, Minimize2, Search, ExternalLink } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Common UK towns in the Sussex area with their coordinates
const TOWN_COORDINATES = {
  'burgess hill': { lat: 50.9536, lng: -0.1286 },
  'hassocks': { lat: 50.9256, lng: -0.1456 },
  'hurstpierpoint': { lat: 50.9323, lng: -0.1789 },
  'brighton': { lat: 50.8225, lng: -0.1372 },
  'hove': { lat: 50.8352, lng: -0.1758 },
  'lewes': { lat: 50.8739, lng: 0.0087 },
  'haywards heath': { lat: 51.0046, lng: -0.1030 },
  'uckfield': { lat: 50.9695, lng: 0.0960 },
  'crowborough': { lat: 51.0592, lng: 0.1631 },
  'eastbourne': { lat: 50.7684, lng: 0.2905 },
  'worthing': { lat: 50.8147, lng: -0.3714 },
  'littlehampton': { lat: 50.8094, lng: -0.5405 },
  'bognor regis': { lat: 50.7829, lng: -0.6762 },
  'chichester': { lat: 50.8365, lng: -0.7792 },
  'horsham': { lat: 51.0621, lng: -0.3254 },
  'crawley': { lat: 51.1092, lng: -0.1872 },
  'east grinstead': { lat: 51.1261, lng: -0.0106 },
  'newhaven': { lat: 50.7936, lng: 0.0545 },
  'seaford': { lat: 50.7701, lng: 0.1039 },
  'peacehaven': { lat: 50.7924, lng: -0.0038 },
  'portslade': { lat: 50.8402, lng: -0.2133 },
  'shoreham': { lat: 50.8343, lng: -0.2743 },
  'steyning': { lat: 50.8872, lng: -0.3283 },
  'henfield': { lat: 50.9312, lng: -0.2643 },
  'ditchling': { lat: 50.9193, lng: -0.1125 },
  'keymer': { lat: 50.9198, lng: -0.1345 },
  'sayers common': { lat: 50.9398, lng: -0.2012 },
  'cuckfield': { lat: 51.0022, lng: -0.1387 },
  'lindfield': { lat: 51.0135, lng: -0.0891 },
  'bolney': { lat: 50.9823, lng: -0.2156 },
  'plumpton': { lat: 50.9093, lng: -0.0712 },
  'wivelsfield': { lat: 50.9412, lng: -0.0867 },
  'london': { lat: 51.5074, lng: -0.1278 },
};

// UK Postcode prefix to town mapping
const POSTCODE_TO_TOWN = {
  'BN1': { town: 'Brighton', lat: 50.8225, lng: -0.1372 },
  'BN2': { town: 'Brighton East', lat: 50.8200, lng: -0.1100 },
  'BN3': { town: 'Hove', lat: 50.8352, lng: -0.1758 },
  'BN5': { town: 'Henfield', lat: 50.9312, lng: -0.2643 },
  'BN6': { town: 'Hassocks', lat: 50.9256, lng: -0.1456 },
  'BN7': { town: 'Lewes', lat: 50.8739, lng: 0.0087 },
  'RH15': { town: 'Burgess Hill', lat: 50.9536, lng: -0.1286 },
  'RH16': { town: 'Haywards Heath', lat: 51.0046, lng: -0.1030 },
};

// Extract town from address
const extractTown = (address) => {
  if (!address) return null;
  const addressLower = address.toLowerCase();
  const addressUpper = address.toUpperCase();
  
  for (const [town, coords] of Object.entries(TOWN_COORDINATES)) {
    if (addressLower.includes(town)) {
      return { town, ...coords };
    }
  }
  
  const postcodeMatch = addressUpper.match(/\b([A-Z]{1,2}\d{1,2}[A-Z]?)\s*\d?[A-Z]{0,2}\b/);
  if (postcodeMatch) {
    const postcodePrefix = postcodeMatch[1];
    if (POSTCODE_TO_TOWN[postcodePrefix]) {
      return { ...POSTCODE_TO_TOWN[postcodePrefix], fromPostcode: postcodePrefix };
    }
  }
  
  return null;
};

const Empire = ({ user, onSelectCustomer }) => {
  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [geoStats, setGeoStats] = useState(null);
  const [companyLogo, setCompanyLogo] = useState(null);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const infoWindowRef = useRef(null);

  const companyName = user?.company_name || user?.name || 'Your Business';
  
  // Fetch company logo from settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const token = localStorage.getItem('streakfree_token');
        const response = await fetch(`${API_URL}/api/settings`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          if (data.company_logo) {
            setCompanyLogo(data.company_logo);
          }
        }
      } catch (err) {
        console.error('Error fetching company logo:', err);
      }
    };
    fetchSettings();
  }, []);

  const handleCustomerClick = (customer) => {
    if (onSelectCustomer) {
      onSelectCustomer(customer);
    }
  };

  // Load Google Maps API
  useEffect(() => {
    if (window.google?.maps) {
      setMapLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => setMapLoaded(true);
    document.head.appendChild(script);
  }, []);

  // Fetch geocoding stats
  const fetchGeoStats = async () => {
    try {
      const token = localStorage.getItem('streakfree_token');
      if (!token) return;
      
      const response = await fetch(`${API_URL}/api/geocode/customers/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setGeoStats(data);
      }
    } catch (error) {
      console.error('Error fetching geo stats:', error);
    }
  };

  // Trigger town-level geocoding
  const handleFindMissingCustomers = async () => {
    try {
      setIsGeocoding(true);
      const token = localStorage.getItem('streakfree_token');
      if (!token) return;
      
      const response = await fetch(`${API_URL}/api/geocode/customers/town`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        setTimeout(async () => {
          await fetchGeoStats();
          window.location.reload();
        }, 2000);
      }
    } catch (error) {
      console.error('Error starting town geocoding:', error);
      setIsGeocoding(false);
    }
  };

  useEffect(() => {
    fetchGeoStats();
  }, []);

  // Fetch customers
  useEffect(() => {
    let mounted = true;
    
    const fetchCustomers = async () => {
      setIsLoading(true);
      
      try {
        const token = localStorage.getItem('streakfree_token');
        if (!token) {
          setIsLoading(false);
          return;
        }
        
        const response = await fetch(`${API_URL}/api/customers?limit=1000&regular_only=true`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (!mounted) return;
        
        if (response.ok) {
          const data = await response.json();
          const customersList = Array.isArray(data) ? data : (data.customers || []);
          
          const processedCustomers = customersList.map(c => {
            if (c.geolocation && c.geolocation.lat && c.geolocation.lng) {
              const isTownLevel = c.geolocation.town_level === true;
              return { 
                ...c, 
                mapLat: c.geolocation.lat, 
                mapLng: c.geolocation.lng, 
                isTownLevel: isTownLevel,
                townName: c.geolocation.town_name || null
              };
            }
            
            const townData = extractTown(c.address);
            if (townData) {
              return { ...c, mapLat: townData.lat, mapLng: townData.lng, isTownLevel: true, townName: townData.town };
            }
            
            return null;
          }).filter(Boolean);
          
          setCustomers(processedCustomers);
        }
      } catch (error) {
        console.error('Error fetching customers:', error);
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    fetchCustomers();
    
    return () => { mounted = false; };
  }, []);

  // Initialize Google Map
  useEffect(() => {
    if (!mapLoaded || !mapRef.current || customers.length === 0) return;

    // Create map if not exists
    if (!mapInstanceRef.current) {
      const avgLat = customers.reduce((sum, c) => sum + c.mapLat, 0) / customers.length;
      const avgLng = customers.reduce((sum, c) => sum + c.mapLng, 0) / customers.length;

      const map = new window.google.maps.Map(mapRef.current, {
        center: { lat: avgLat || 50.9256, lng: avgLng || -0.1456 },
        zoom: 11,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        styles: [
          { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] }
        ]
      });

      mapInstanceRef.current = map;
      infoWindowRef.current = new window.google.maps.InfoWindow();
    }
    
    // Clear existing markers
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];

    // Get the map instance
    const map = mapInstanceRef.current;

    // Add markers
    const bounds = new window.google.maps.LatLngBounds();
    
    customers.forEach(customer => {
      const color = customer.isTownLevel ? '#f59e0b' : '#10b981';
      
      // Small round pins — `companyLogo` raster images render as squares
      // and clutter the map when zoomed out. The circle SVG path stays
      // crisp at every zoom level and visually matches Squeegee's UX.
      // Town-level rollup pins are slightly smaller (scale 5) so they
      // visually defer to the individual customer pins (scale 6) when both
      // are visible on the same map.
      const markerIcon = {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: customer.isTownLevel ? 5 : 6,
        fillColor: color,
        fillOpacity: 0.95,
        strokeColor: '#ffffff',
        strokeWeight: 1.5,
      };
      
      const marker = new window.google.maps.Marker({
        position: { lat: customer.mapLat, lng: customer.mapLng },
        map: map,
        icon: markerIcon
      });

      marker.addListener('click', () => {
        setSelectedCustomer(customer);
        
        // Create info window content with clickable customer name
        const contentDiv = document.createElement('div');
        contentDiv.style.fontFamily = 'system-ui';
        contentDiv.style.minWidth = '150px';
        contentDiv.style.padding = '4px';
        
        const nameDiv = document.createElement('div');
        nameDiv.style.color = '#10b981';
        nameDiv.style.fontWeight = '600';
        nameDiv.style.cursor = 'pointer';
        nameDiv.style.marginBottom = '4px';
        nameDiv.textContent = customer.name || 'Unknown';
        nameDiv.onclick = () => {
          infoWindowRef.current.close();
          handleCustomerClick(customer);
        };
        
        const addressSpan = document.createElement('span');
        addressSpan.style.color = '#666';
        addressSpan.style.fontSize = '12px';
        addressSpan.textContent = customer.address || '';
        
        contentDiv.appendChild(nameDiv);
        contentDiv.appendChild(addressSpan);
        
        if (customer.isTownLevel) {
          const locationNote = document.createElement('div');
          locationNote.style.color = '#f59e0b';
          locationNote.style.fontSize = '11px';
          locationNote.style.marginTop = '4px';
          locationNote.textContent = `(Town: ${customer.townName})`;
          contentDiv.appendChild(locationNote);
        }
        
        infoWindowRef.current.setContent(contentDiv);
        infoWindowRef.current.open(map, marker);
      });

      markersRef.current.push(marker);
      bounds.extend({ lat: customer.mapLat, lng: customer.mapLng });
    });

    if (customers.length > 0) {
      map.fitBounds(bounds, 30);
    }

  }, [mapLoaded, customers]);

  // Handle fullscreen resize
  useEffect(() => {
    if (mapInstanceRef.current) {
      setTimeout(() => {
        window.google?.maps?.event?.trigger(mapInstanceRef.current, 'resize');
      }, 100);
    }
  }, [isFullscreen]);

  const handleZoomIn = () => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.setZoom(mapInstanceRef.current.getZoom() + 1);
    }
  };

  const handleZoomOut = () => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.setZoom(mapInstanceRef.current.getZoom() - 1);
    }
  };

  const toggleFullscreen = () => setIsFullscreen(!isFullscreen);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full bg-zinc-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-emerald-500 mx-auto mb-4" />
          <p className="text-zinc-600">Loading {companyName} Empire...</p>
        </div>
      </div>
    );
  }

  const exactCount = customers.filter(c => !c.isTownLevel).length;
  const townCount = customers.filter(c => c.isTownLevel).length;
  const missingCount = geoStats?.needs_geocoding || 0;

  return (
    <div className={`${isFullscreen ? 'fixed inset-0 z-30' : 'h-full'} flex flex-col bg-white`}>
      {/* Header */}
      <div className="flex-shrink-0 p-4 border-b border-zinc-100 flex items-center justify-between bg-white">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
            <MapPin size={20} className="text-emerald-600" />
          </div>
          <div>
            <h1 className="font-semibold text-zinc-900">{companyName} Empire</h1>
            <p className="text-xs text-zinc-500">
              {customers.length} customers ({exactCount} exact, {townCount} town)
              {missingCount > 0 && <span className="text-amber-600"> • {missingCount} missing</span>}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {missingCount > 0 && (
            <button 
              onClick={handleFindMissingCustomers} 
              disabled={isGeocoding}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-100 hover:bg-amber-200 text-amber-700 rounded-lg text-sm font-medium disabled:opacity-50"
            >
              {isGeocoding ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
              <span className="hidden sm:inline">Find Missing</span>
            </button>
          )}
          <button onClick={handleZoomOut} className="p-2 hover:bg-zinc-100 rounded-lg">
            <ZoomOut size={20} className="text-zinc-600" />
          </button>
          <button onClick={handleZoomIn} className="p-2 hover:bg-zinc-100 rounded-lg">
            <ZoomIn size={20} className="text-zinc-600" />
          </button>
          <button onClick={toggleFullscreen} className="p-2 hover:bg-zinc-100 rounded-lg">
            {isFullscreen ? <Minimize2 size={20} className="text-zinc-600" /> : <Maximize2 size={20} className="text-zinc-600" />}
          </button>
        </div>
      </div>

      {/* Map */}
      <div className="flex-1 relative">
        {customers.length === 0 ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-zinc-50">
            <MapPin size={64} className="text-zinc-300 mb-4" />
            <p className="text-zinc-500 font-medium text-lg">No customer locations yet</p>
          </div>
        ) : (
          <div ref={mapRef} className="absolute inset-0" />
        )}
      </div>

      {/* Selected Customer Panel */}
      {selectedCustomer && (
        <div className="flex-shrink-0 p-4 border-t border-zinc-100 bg-zinc-50">
          <div className="flex items-center justify-between">
            <div 
              className="cursor-pointer group"
              onClick={() => handleCustomerClick(selectedCustomer)}
            >
              <h3 className="font-semibold text-emerald-600 group-hover:text-emerald-700 flex items-center gap-1.5">
                {selectedCustomer.name}
                <ExternalLink size={14} className="opacity-50 group-hover:opacity-100" />
              </h3>
              <p className="text-sm text-zinc-500">{selectedCustomer.address}</p>
              {selectedCustomer.isTownLevel && (
                <p className="text-xs text-amber-600">Location: {selectedCustomer.townName} (town level)</p>
              )}
            </div>
            <button
              onClick={() => {
                const url = `https://www.google.com/maps/dir/?api=1&destination=${selectedCustomer.mapLat},${selectedCustomer.mapLng}`;
                const link = document.createElement('a');
                link.href = url;
                link.target = '_blank';
                link.rel = 'noopener noreferrer';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
              className="px-4 py-2 bg-emerald-500 text-white rounded-lg text-sm font-medium hover:bg-emerald-600"
            >
              Navigate
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Empire;
